import React, { useCallback, useEffect, useRef, useState } from 'react';
import lamejs from '@breezystack/lamejs';

/* ── MP3 encoder (primary) ─────────────────────────── */
function encodeMp3(samples, sampleRate) {
  const mp3enc = new lamejs.Mp3Encoder(1, sampleRate, 128);
  const blockSize = 1152;
  const int16 = new Int16Array(samples.length);

  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }

  const mp3Parts = [];
  for (let i = 0; i < int16.length; i += blockSize) {
    const chunk = int16.subarray(i, i + blockSize);
    const buf = mp3enc.encodeBuffer(chunk);
    if (buf.length > 0) mp3Parts.push(buf);
  }

  const end = mp3enc.flush();
  if (end.length > 0) mp3Parts.push(end);

  return new Blob(mp3Parts, { type: 'audio/mp3' });
}

/* ── Pick best MediaRecorder MIME (fallback) ───────── */
const FALLBACK_MIMES = [
  'audio/mp4;codecs=aac',
  'audio/webm;codecs=opus',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/webm',
];

function pickMediaRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of FALLBACK_MIMES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return '';
}

function extFromMime(mime) {
  if (mime.startsWith('audio/mp4')) return 'mp4';
  if (mime.startsWith('audio/ogg')) return 'ogg';
  return 'webm';
}

/* ── Component ─────────────────────────────────────── */
function AudioRecorder({ onRecorded, disabled, existingAudioUrl }) {
  const [state, setState] = useState(existingAudioUrl ? 'existing' : 'idle');
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const timerRef = useRef(null);
  const modeRef = useRef('primary'); // 'primary' (WebAudio+MP3) | 'fallback' (MediaRecorder)

  // React to existingAudioUrl prop changes (when navigating between questions)
  useEffect(() => {
    if (existingAudioUrl && state !== 'recording' && state !== 'recorded') {
      setState('existing');
    } else if (!existingAudioUrl && state === 'existing') {
      setState('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAudioUrl]);

  // Primary refs (Web Audio + MP3)
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  // Fallback refs (MediaRecorder)
  const mediaRecorderRef = useRef(null);
  const mrChunksRef = useRef([]);
  const mrStreamRef = useRef(null);
  const mrMimeRef = useRef('');

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  /* ── Start: try primary, fall back to MediaRecorder ── */
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });

      // Try primary path: Web Audio API + MP3
      let usePrimary = true;
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') await ctx.resume();

        const source = ctx.createMediaStreamSource(stream);
        const processor = ctx.createScriptProcessor(4096, 1, 1);

        // Quick check that lamejs works
        const testEnc = new lamejs.Mp3Encoder(1, ctx.sampleRate, 128);
        testEnc.flush();

        chunksRef.current = [];
        processor.onaudioprocess = (e) => {
          const input = e.inputBuffer.getChannelData(0);
          chunksRef.current.push(Float32Array.from(input));
        };

        source.connect(processor);
        processor.connect(ctx.destination);

        audioContextRef.current = ctx;
        sourceRef.current = source;
        processorRef.current = processor;
        streamRef.current = stream;
        modeRef.current = 'primary';
      } catch {
        usePrimary = false;
      }

      // Fallback: MediaRecorder
      if (!usePrimary) {
        const mime = pickMediaRecorderMime();
        if (mime === null) {
          stream.getTracks().forEach((t) => t.stop());
          return; // no recording support at all
        }

        const options = mime ? { mimeType: mime } : {};
        const recorder = new MediaRecorder(stream, options);
        mrMimeRef.current = recorder.mimeType;
        mrChunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) mrChunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(mrChunksRef.current, { type: recorder.mimeType });
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
          setState('recorded');
          onRecorded(blob);
        };

        mediaRecorderRef.current = recorder;
        mrStreamRef.current = stream;
        recorder.start(250);
        modeRef.current = 'fallback';
      }

      setState('recording');
      setDuration(0);
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    } catch {
      // microphone permission denied
    }
  }, [onRecorded]);

  /* ── Stop ─────────────────────────────────────────── */
  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (modeRef.current === 'primary') {
      // Disconnect Web Audio nodes
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current.onaudioprocess = null;
        processorRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      const sampleRate = audioContextRef.current?.sampleRate || 44100;
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // Merge chunks and encode MP3
      const totalLength = chunksRef.current.reduce((acc, c) => acc + c.length, 0);
      const merged = new Float32Array(totalLength);
      let offset = 0;
      for (const chunk of chunksRef.current) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }
      chunksRef.current = [];

      const blob = encodeMp3(merged, sampleRate);
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setState('recorded');
      onRecorded(blob);
    } else {
      // MediaRecorder — stop triggers onstop callback which handles the rest
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    }
  }, [onRecorded]);

  /* ── Reset ────────────────────────────────────────── */
  const reset = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setState('idle');
    setDuration(0);
    onRecorded(null);
  }, [audioUrl, onRecorded]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="audio-recorder">
      {state === 'existing' && (
        <div className="audio-recorder__preview">
          <audio src={existingAudioUrl} controls preload="auto" className="audio-recorder__player" />
          <button
            type="button"
            className="audio-recorder__btn audio-recorder__btn--reset"
            onClick={() => setState('idle')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
            Recommencer
          </button>
        </div>
      )}

      {state === 'idle' && (
        <button
          type="button"
          className="audio-recorder__btn audio-recorder__btn--start"
          onClick={startRecording}
          disabled={disabled}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
          Enregistrer un audio
        </button>
      )}

      {state === 'recording' && (
        <div className="audio-recorder__active">
          <span className="audio-recorder__pulse" />
          <span className="audio-recorder__time">{formatTime(duration)}</span>
          <button
            type="button"
            className="audio-recorder__btn audio-recorder__btn--stop"
            onClick={stopRecording}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <rect x="4" y="4" width="16" height="16" rx="2" />
            </svg>
            {`Arr\u00eater`}
          </button>
        </div>
      )}

      {state === 'recorded' && (
        <div className="audio-recorder__preview">
          <audio src={audioUrl} controls className="audio-recorder__player" />
          <button
            type="button"
            className="audio-recorder__btn audio-recorder__btn--reset"
            onClick={reset}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
            Recommencer
          </button>
        </div>
      )}
    </div>
  );
}

export default AudioRecorder;
